<script setup lang="ts">
/**
 * A generated control for a `list` parameter of per-row ratio lists (plan §4.4, FND-21): the
 * offset grid's dividers per row. One row per entry, listed top to bottom; each divider is a
 * position field in mm from the left (stored as a ratio of W) with Remove, and each row has
 * Add a divider. Every edit goes through the family (setParam) and is refused beside the row
 * when the result would be invalid.
 */
import { computed } from 'vue';
import type { ParameterDef } from '@/families/types';
import { fmtMm, parseMm } from '@/domain';
import Icon from './Icon.vue';
import { snap, ui } from '@/app/state';
import { setRowList } from '@/app/edits';
import { focusSoon } from '@/app/navigation';

const props = defineProps<{ p: ParameterDef }>();
const key = computed(() => props.p.key);
const W = computed(() => snap.value.design.W);
/** The per-row lists; a list not set yet (null) has one empty row per row or tier of the design. */
const rows = computed<number[][]>(() => {
  const params = snap.value.design.params ?? {};
  const v = params[key.value];
  if (Array.isArray(v)) return (v as unknown[]).map((r) => (Array.isArray(r) ? (r as number[]).filter((x) => typeof x === 'number') : []));
  const count = Number(params.rows ?? params.tiers ?? 0);
  return Array.from({ length: Number.isFinite(count) ? count : 0 }, () => []);
});
/** "Row" for the offset grid, "Tier" for the staggered shelf (from the parameter's label). */
const word = computed(() => (/tier/i.test(props.p.ui.label) ? 'Tier' : 'Row'));
const max = computed(() => props.p.range?.max ?? 4);
const n = computed(() => rows.value.length);
/** Rows top to bottom, as the piece stands. */
const shown = computed(() => rows.value.map((xs, i) => ({ i, xs })).reverse());
const rowName = (i: number) => `${word.value} ${i + 1}${i === n.value - 1 && n.value > 1 ? ' (top)' : i === 0 && n.value > 1 ? ' (bottom)' : ''}`;
const toTicks = (x: number) => Math.round(x * W.value);
const draftId = (row: number, j: number) => `rd-${row}-${j}`;
const val = (row: number, j: number, x: number) => (draftId(row, j) in ui.drafts ? ui.drafts[draftId(row, j)]! : fmtMm(toTicks(x)));

function commit(row: number, j: number, raw: string) {
  const r = parseMm(raw);
  const errKey = `${key.value}-${row}`;
  if (!r.ok) { ui.fieldErrors[errKey] = r.error; return; }
  if (r.ticks <= 0 || r.ticks >= W.value) { ui.fieldErrors[errKey] = `A divider stands inside the case: between 0 and ${fmtMm(W.value)} mm from the left.`; return; }
  const xs = rows.value[row]!.slice();
  xs[j] = r.ticks / W.value;
  if (setRowList(key.value, row, xs, `${word.value} ${row + 1}: divider at ${fmtMm(r.ticks)} mm`, n.value)) delete ui.drafts[draftId(row, j)];
}
function onKey(e: KeyboardEvent, row: number, j: number) {
  const el = e.target as HTMLInputElement;
  if (e.key === 'Enter') { e.preventDefault(); commit(row, j, el.value); return; }
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  const r = parseMm(el.value);
  if (!r.ok) return;
  e.preventDefault();
  const next = fmtMm(r.ticks + (e.shiftKey ? 100 : 10) * (e.key === 'ArrowUp' ? 1 : -1));
  ui.drafts[draftId(row, j)] = next;
  el.value = next;
  commit(row, j, next);
}
function remove(row: number, j: number) {
  const xs = rows.value[row]!.filter((_x, k) => k !== j);
  focusSoon(`[data-row-add="${row}"]`);
  setRowList(key.value, row, xs, `${word.value} ${row + 1}: divider removed`, n.value);
}
/** A new divider goes in the middle of the row's widest cell. */
function add(row: number) {
  const xs = rows.value[row]!.slice().sort((a, b) => a - b);
  const edges = [0, ...xs, 1];
  let best = 0;
  for (let k = 1; k < edges.length - 1; k++) if (edges[k + 1]! - edges[k]! > edges[best + 1]! - edges[best]!) best = k;
  const x = (edges[best]! + edges[best + 1]!) / 2;
  const at = [...xs, x].sort((a, b) => a - b).indexOf(x);
  focusSoon(`#${draftId(row, at)}`);
  setRowList(key.value, row, [...xs, x], `${word.value} ${row + 1}: divider added at ${fmtMm(toTicks(x))} mm`, n.value);
}
</script>

<template>
  <div class="rowlist-ctl" :data-rowlist="key">
    <h3 :id="`ph-${key}`" class="h-sub">{{ p.ui.label }}</h3>
    <p class="hint">Millimetres from the left edge to the divider’s centre. {{ p.ui.help }}</p>
    <ol class="rowdivs" :aria-labelledby="`ph-${key}`">
      <li v-for="r in shown" :key="r.i" class="rowdiv" :data-row="r.i + 1">
        <div class="rowdiv-head">
          <strong :id="`rowh-${r.i}`">{{ rowName(r.i) }}</strong>
          <button type="button" class="btn btn--sm btn--quiet" :data-row-add="r.i" :disabled="r.xs.length >= max" :aria-label="`Add a divider to ${word.toLowerCase()} ${r.i + 1}`" @click="add(r.i)"><Icon name="plus" /> Add a divider</button>
        </div>
        <div class="rowdiv-list" role="group" :aria-labelledby="`rowh-${r.i}`">
          <span v-if="!r.xs.length" class="rowdiv-none">No divider: one open cell</span>
          <span v-for="(x, j) in r.xs" :key="j" class="rowdiv-item">
            <label class="sr-only" :for="draftId(r.i, j)">{{ word }} {{ r.i + 1 }}, divider {{ j + 1 }}, mm from the left</label>
            <span class="input-unit input-unit--sm">
              <input
                :id="draftId(r.i, j)"
                class="input mono"
                inputmode="decimal"
                autocomplete="off"
                :value="val(r.i, j, x)"
                :aria-invalid="ui.fieldErrors[`${key}-${r.i}`] ? 'true' : undefined"
                :aria-describedby="ui.fieldErrors[`${key}-${r.i}`] ? `err-${key}-${r.i}` : undefined"
                @input="ui.drafts[draftId(r.i, j)] = ($event.target as HTMLInputElement).value"
                @change="($event.target as HTMLInputElement).isConnected && commit(r.i, j, ($event.target as HTMLInputElement).value)"
                @keydown="onKey($event, r.i, j)"
              ><span>mm</span>
            </span>
            <button type="button" class="icon-btn icon-btn--sm" :data-row-remove="`${r.i}-${j}`" :aria-label="`Remove divider ${j + 1} in ${word.toLowerCase()} ${r.i + 1}`" @click="remove(r.i, j)"><Icon name="close" /></button>
          </span>
        </div>
        <p v-if="ui.fieldErrors[`${key}-${r.i}`]" :id="`err-${key}-${r.i}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ ui.fieldErrors[`${key}-${r.i}`] }}</span></p>
      </li>
    </ol>
  </div>
</template>
